import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  Filter, 
  Calendar, 
  Users, 
  FileText,
  CheckCircle2,
  AlertCircle,
  X
} from 'lucide-react';

/**
 * SessionSelector - Multi-select interface for choosing sessions across programs
 * 
 * Features:
 * - Program-level filtering and selection
 * - Session-level selection with SO counts
 * - Search and filter capabilities
 * - Bulk select/deselect operations
 * - Impact preview showing affected SOs
 */
const SessionSelector = ({ 
  selectedSessions = [], 
  onSessionsChange,
  onImpactChange,
  className = ""
}) => {
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPrograms, setSelectedPrograms] = useState(new Set());
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // Load programs and sessions data
  useEffect(() => {
    const loadSessionsData = async () => {
      try {
        setLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Mock programs data
        const mockPrograms = [
          {
            id: 'prog1',
            name: 'Self-Care for Burnout',
            code: 'SCB',
            sessionCount: 4,
            activeSOs: 12
          },
          {
            id: 'prog2', 
            name: 'Healthy Boundaries at Work',
            code: 'HBW',
            sessionCount: 3,
            activeSOs: 8
          },
          {
            id: 'prog3',
            name: 'Leadership Essentials', 
            code: 'LE',
            sessionCount: 6,
            activeSOs: 15
          },
          {
            id: 'prog4',
            name: 'Communication Skills',
            code: 'CS', 
            sessionCount: 5,
            activeSOs: 20
          }
        ];

        // Mock sessions data
        const mockSessions = [
          // SCB Sessions
          { id: 'scb-s1', programId: 'prog1', programName: 'Self-Care for Burnout', sessionName: 'Session 1: Understanding Burnout', activeSOs: 3, materialsCount: 5, lastUpdated: '2024-12-10' },
          { id: 'scb-s2', programId: 'prog1', programName: 'Self-Care for Burnout', sessionName: 'Session 2: Recognizing Signs', activeSOs: 3, materialsCount: 7, lastUpdated: '2024-12-08' },
          { id: 'scb-s3', programId: 'prog1', programName: 'Self-Care for Burnout', sessionName: 'Session 3: Self-Care Strategies', activeSOs: 3, materialsCount: 4, lastUpdated: '2024-12-05' },
          { id: 'scb-s4', programId: 'prog1', programName: 'Self-Care for Burnout', sessionName: 'Session 4: Building Resilience', activeSOs: 3, materialsCount: 6, lastUpdated: '2024-12-03' },

          // HBW Sessions  
          { id: 'hbw-s1', programId: 'prog2', programName: 'Healthy Boundaries at Work', sessionName: 'Session 1: What Are Boundaries?', activeSOs: 3, materialsCount: 4, lastUpdated: '2024-12-09' },
          { id: 'hbw-s2', programId: 'prog2', programName: 'Healthy Boundaries at Work', sessionName: 'Session 2: Setting Boundaries', activeSOs: 3, materialsCount: 5, lastUpdated: '2024-12-07' },
          { id: 'hbw-s3', programId: 'prog2', programName: 'Healthy Boundaries at Work', sessionName: 'Session 3: Maintaining Boundaries', activeSOs: 2, materialsCount: 3, lastUpdated: '2024-12-04' },

          // LE Sessions
          { id: 'le-s1', programId: 'prog3', programName: 'Leadership Essentials', sessionName: 'Session 1: Leadership Foundations', activeSOs: 3, materialsCount: 8, lastUpdated: '2024-12-11' },
          { id: 'le-s2', programId: 'prog3', programName: 'Leadership Essentials', sessionName: 'Session 2: Communication Skills', activeSOs: 2, materialsCount: 6, lastUpdated: '2024-12-09' },
          { id: 'le-s3', programId: 'prog3', programName: 'Leadership Essentials', sessionName: 'Session 3: Team Building', activeSOs: 2, materialsCount: 7, lastUpdated: '2024-12-06' },
          { id: 'le-s4', programId: 'prog3', programName: 'Leadership Essentials', sessionName: 'Session 4: Conflict Resolution', activeSOs: 3, materialsCount: 5, lastUpdated: '2024-12-02' },
          { id: 'le-s5', programId: 'prog3', programName: 'Leadership Essentials', sessionName: 'Session 5: Decision Making', activeSOs: 2, materialsCount: 4, lastUpdated: '2024-11-30' },
          { id: 'le-s6', programId: 'prog3', programName: 'Leadership Essentials', sessionName: 'Session 6: Performance Management', activeSOs: 3, materialsCount: 9, lastUpdated: '2024-11-28' },

          // CS Sessions
          { id: 'cs-s1', programId: 'prog4', programName: 'Communication Skills', sessionName: 'Session 1: Active Listening', activeSOs: 4, materialsCount: 3, lastUpdated: '2024-12-10' },
          { id: 'cs-s2', programId: 'prog4', programName: 'Communication Skills', sessionName: 'Session 2: Verbal Communication', activeSOs: 4, materialsCount: 4, lastUpdated: '2024-12-08' },
          { id: 'cs-s3', programId: 'prog4', programName: 'Communication Skills', sessionName: 'Session 3: Non-Verbal Communication', activeSOs: 4, materialsCount: 5, lastUpdated: '2024-12-05' },
          { id: 'cs-s4', programId: 'prog4', programName: 'Communication Skills', sessionName: 'Session 4: Written Communication', activeSOs: 4, materialsCount: 6, lastUpdated: '2024-12-03' },
          { id: 'cs-s5', programId: 'prog4', programName: 'Communication Skills', sessionName: 'Session 5: Digital Communication', activeSOs: 4, materialsCount: 4, lastUpdated: '2024-12-01' }
        ];

        setPrograms(mockPrograms);
        setSessions(mockSessions);
        setFilteredSessions(mockSessions);
        
      } catch (err) {
        console.error('Failed to load sessions data:', err);
        setError('Failed to load sessions data');
      } finally {
        setLoading(false);
      }
    };

    loadSessionsData();
  }, []);

  // Filter sessions based on search, program selection, and show only selected
  useEffect(() => {
    let filtered = sessions;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(session => 
        session.sessionName.toLowerCase().includes(query) ||
        session.programName.toLowerCase().includes(query)
      );
    }

    // Filter by selected programs
    if (selectedPrograms.size > 0) {
      filtered = filtered.filter(session => 
        selectedPrograms.has(session.programId)
      );
    }

    // Filter to show only selected sessions
    if (showOnlySelected) {
      const selectedSet = new Set(selectedSessions);
      filtered = filtered.filter(session => 
        selectedSet.has(session.id)
      );
    }

    setFilteredSessions(filtered);
  }, [searchQuery, selectedPrograms, showOnlySelected, sessions, selectedSessions]);

  // Calculate impact metrics whenever selection changes
  useEffect(() => {
    const totalSOs = selectedSessions.reduce((sum, sessionId) => {
      const session = sessions.find(s => s.id === sessionId);
      return sum + (session?.activeSOs || 0);
    }, 0);

    const totalMaterials = selectedSessions.reduce((sum, sessionId) => {
      const session = sessions.find(s => s.id === sessionId);
      return sum + (session?.materialsCount || 0);
    }, 0);

    const affectedPrograms = new Set(
      selectedSessions.map(sessionId => {
        const session = sessions.find(s => s.id === sessionId);
        return session?.programId;
      }).filter(Boolean)
    );

    if (onImpactChange) {
      onImpactChange({
        selectedSessionsCount: selectedSessions.length,
        totalSOsAffected: totalSOs,
        totalMaterialsAffected: totalMaterials,
        affectedProgramsCount: affectedPrograms.size
      });
    }
  }, [selectedSessions, sessions, onImpactChange]);

  const handleSessionToggle = (sessionId) => {
    const newSelected = selectedSessions.includes(sessionId)
      ? selectedSessions.filter(id => id !== sessionId)
      : [...selectedSessions, sessionId];
    
    onSessionsChange(newSelected);
  };

  const handleProgramToggle = (programId) => {
    const newSelectedPrograms = new Set(selectedPrograms);
    if (newSelectedPrograms.has(programId)) {
      newSelectedPrograms.delete(programId);
    } else {
      newSelectedPrograms.add(programId);
    }
    setSelectedPrograms(newSelectedPrograms);
  };

  const handleSelectAllInProgram = (programId) => {
    const programSessions = sessions
      .filter(session => session.programId === programId)
      .map(session => session.id);
    
    const allSelected = programSessions.every(id => selectedSessions.includes(id));
    
    if (allSelected) {
      // Deselect all sessions in this program
      const newSelected = selectedSessions.filter(id => !programSessions.includes(id));
      onSessionsChange(newSelected);
    } else {
      // Select all sessions in this program
      const newSelected = [...new Set([...selectedSessions, ...programSessions])];
      onSessionsChange(newSelected);
    }
  };

  const handleSelectAll = () => {
    if (selectedSessions.length === filteredSessions.length) {
      onSessionsChange([]);
    } else {
      onSessionsChange(filteredSessions.map(session => session.id));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-80" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex space-x-2">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-20" />
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="border rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-5 w-64" />
                </div>
                <Skeleton className="h-4 w-48" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-600 font-medium">Failed to load sessions</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outline" 
            className="mt-4"
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const groupedSessions = programs.map(program => ({
    ...program,
    sessions: filteredSessions.filter(session => session.programId === program.id)
  })).filter(program => program.sessions.length > 0);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Select Sessions</span>
          </div>
          {selectedSessions.length > 0 && (
            <Badge variant="default" className="bg-blue-100 text-blue-800">
              {selectedSessions.length} selected
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Choose sessions across programs for bulk materials operations
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        {/* Search and Filter Controls */}
        <div className="space-y-4 mb-6">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search programs or sessions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
              className="flex items-center space-x-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              <span>
                {selectedSessions.length === filteredSessions.length ? 'Deselect All' : 'Select All'}
              </span>
            </Button>
          </div>

          {/* Filter Options */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={showOnlySelected ? "default" : "outline"}
              size="sm"
              onClick={() => setShowOnlySelected(!showOnlySelected)}
              className="flex items-center space-x-1"
            >
              <Filter className="h-3 w-3" />
              <span>Selected Only</span>
            </Button>

            {programs.map(program => (
              <Button
                key={program.id}
                variant={selectedPrograms.has(program.id) ? "default" : "outline"}
                size="sm"
                onClick={() => handleProgramToggle(program.id)}
                className="text-xs"
              >
                {program.code}
              </Button>
            ))}

            {(selectedPrograms.size > 0 || showOnlySelected) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedPrograms(new Set());
                  setShowOnlySelected(false);
                }}
                className="flex items-center space-x-1 text-gray-500"
              >
                <X className="h-3 w-3" />
                <span>Clear Filters</span>
              </Button>
            )}
          </div>
        </div>

        {/* Session Selection by Program */}
        <div className="space-y-6">
          {groupedSessions.map(program => {
            const programSessions = program.sessions;
            const selectedInProgram = programSessions.filter(session => 
              selectedSessions.includes(session.id)
            ).length;
            const allSelectedInProgram = selectedInProgram === programSessions.length && programSessions.length > 0;

            return (
              <div key={program.id} className="space-y-3">
                {/* Program Header */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      checked={allSelectedInProgram}
                      onCheckedChange={() => handleSelectAllInProgram(program.id)}
                      className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900">{program.name}</h3>
                        <Badge variant="secondary" className="text-xs">{program.code}</Badge>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center space-x-1">
                          <Calendar className="h-3 w-3" />
                          <span>{program.sessionCount} sessions</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Users className="h-3 w-3" />
                          <span>{program.activeSOs} active SOs</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {selectedInProgram}/{programSessions.length} selected
                  </div>
                </div>

                {/* Program Sessions */}
                <div className="ml-6 space-y-2">
                  {programSessions.map(session => {
                    const isSelected = selectedSessions.includes(session.id);
                    
                    return (
                      <div
                        key={session.id}
                        className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                          isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleSessionToggle(session.id)}
                            className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 mb-1">
                              {session.sessionName}
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span className="flex items-center space-x-1">
                                <Users className="h-3 w-3" />
                                <span>{session.activeSOs} SOs</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <FileText className="h-3 w-3" />
                                <span>{session.materialsCount} materials</span>
                              </span>
                              <span>Updated {formatDate(session.lastUpdated)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {filteredSessions.length === 0 && (
          <div className="text-center py-8">
            <Search className="h-6 w-6 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No sessions found matching your criteria</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SessionSelector;